import React, { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";

import Amplify, { Auth } from "aws-amplify";
import { CognitoUser, CognitoUserSession, CognitoIdToken, CognitoRefreshToken, CognitoAccessToken, CognitoUserPool } from "amazon-cognito-identity-js";

import { AppEvent, appEventEmitter, triggerAppEvent, useAppContext } from "./app.context";
import {
  VerificationCodeRequest,
  LoginRequest,
  RegisterRequest,
  VerifyResetRequest,
  VerifyRegisterRequest,
  ChangePasswordRequest,
} from "../models/login-request.model";
import errorMessages from "../translations/en/errors.json";
import usePersistedState from "../hooks/persisted-state.hook";
import { AuthSession } from "../models/auth-session.model";
import { useLocation, useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import queryString from "query-string";
import axios from "axios";
import environment from "../env";

export interface AuthContext {
  session?: AuthSession | null;
  setSession(session: (AuthSession & { username: string }) | null): void;
  login(request: LoginRequest): Promise<void>;
  register(request: RegisterRequest): Promise<void>;
  refresh(): Promise<AuthSession>;
  verifySignUp(request: VerifyRegisterRequest): Promise<void>;
  resendSignUpCode(request: VerificationCodeRequest): Promise<void>;
  resetPassword(request: VerificationCodeRequest): Promise<void>;
  verifyResetPassword(request: VerifyResetRequest): Promise<void>;
  changePassword(request: ChangePasswordRequest): Promise<void>;
  signOut(): Promise<void>;
  isAuthReady: boolean;
}

const Context = createContext<AuthContext>(null!);

const { defaultClientId, idpClientId, userPoolId, domain: cognitoDomain } = environment.cognito;

function AuthContextProvider(props: React.PropsWithChildren<{}>): JSX.Element {
  // Check params for auth
  const history = useHistory();
  const { search } = useLocation();
  const queryClient = useQueryClient();

  const [authParams] = useState<{ code?: string; }>(
    queryString.parse(search)
  );

  const [isAuthReady, setIsAuthReady] = useState(false);

  const [session, setSession] = usePersistedState<(AuthSession & { username: string }) | null>("context/session", {
    storageType: "session",
    priority: 1, // This should be the highest priory in the app as it is needed to get in
    clearStateHandler: { emitter: appEventEmitter, event: AppEvent.signOut },
  });

  const sessionRef = useRef(session || null);
  const setSessionRef = useRef(setSession);
  useEffect(() => {
    setSessionRef.current = setSession;
  }, [setSession]);

  useEffect(() => {
    setIsAuthReady(false);
    let userClientType = "app";
    if (sessionRef.current) {
      const { clientType } = sessionRef.current!;
      userClientType = clientType
    }
    const awsConfig = {
      Analytics: {
        disabled: true,
      },
      Auth: {
        oauth: {
          // domain: process.env.REACT_APP_OAUTH_DOMAIN,
          scope: ["email"],
          // redirectSignIn: process.env.REACT_APP_SIGNIN_CALLBACK,
          // redirectSignOut: process.env.REACT_APP_SIGNOUT_CALLBACK,
          responseType: "code",
        },
        region: "us-east-1",
        userPoolId: userPoolId,
        userPoolWebClientId: userClientType === "microsoftIdp" && idpClientId ? idpClientId : defaultClientId,
        storage: window.sessionStorage,
        authenticationFlowType: "USER_PASSWORD_AUTH"
      },
    };

    Auth.configure(awsConfig);

    const hasAuthParams = Boolean(authParams?.code);

    // If we have auth params set up the Cognito session with those.
    // The OAuth code-exchange requires idpClientId – if it is not configured
    // for this environment, discard the code param and fall through to the
    // standard login page.
    if (hasAuthParams && idpClientId) {
      history.replace("");

      const urlencoded = new URLSearchParams();
      urlencoded.append("grant_type", "authorization_code");
      urlencoded.append("client_id", idpClientId);
      urlencoded.append("code", authParams.code!);
      urlencoded.append("redirect_uri", window.location.origin);

      const requestOptions: RequestInit = {
        method: "POST",
        body: urlencoded,
        redirect: "follow"
      };

      fetch(`${cognitoDomain}/oauth2/token`, requestOptions)
        .then((response) => response.json())
        .then((result) => {
          if (!result.id_token || !result.access_token || !result.refresh_token) {
            return Promise.reject(new Error("Invalid token response: missing required fields"));
          }
          const localSession = new CognitoUserSession({
            IdToken: new CognitoIdToken({ IdToken: result.id_token }),
            RefreshToken: new CognitoRefreshToken({ RefreshToken: result.refresh_token }),
            AccessToken: new CognitoAccessToken({ AccessToken: result.access_token }),
          });

          setSessionRef.current({
            username: localSession.getAccessToken().payload.username,
            clientType: "microsoftIdp",
            accessToken: localSession.getIdToken().getJwtToken(),
            issuedAt: localSession.getIdToken().getIssuedAt(),
            expiresIn: localSession.getIdToken().getExpiration(),
            refreshToken: localSession.getRefreshToken().getToken(),
          });
        })
        .catch((error) => console.error(error));
    } else if (hasAuthParams && !idpClientId) {
      // IDP not configured for this environment – drop the stale code param
      // and send the user to the login page.
      history.replace("/login");
      setIsAuthReady(true);
    } else {
      const response = Auth.currentSession()
        .catch((e) => null)
        .then((response) => {
          if (response) {
            const accessToken = response.getIdToken().getJwtToken();
            const username = response.getAccessToken().payload.username;

            // Only set session if access token has changed.
            setSessionRef.current((prev) => {
              const clientType = sessionRef.current?.clientType ? sessionRef.current.clientType : "app";
              if (prev?.accessToken === accessToken) {
                return { ...prev, clientType };
              }
              return {
                username,
                clientType,
                accessToken: accessToken,
                expiresIn: response.getIdToken().getExpiration(),
                issuedAt: response.getIdToken().getIssuedAt(),
                refreshToken: response.getRefreshToken().getToken(),
              };
            });
          }

          return;
        });
      response.finally(() => setIsAuthReady(true));
    }
  }, [authParams, history]);

  useEffect(() => {
    sessionRef.current = session || null;
  }, [session]);

  const completeUpdatePassword = useCallback((loginRequest: LoginRequest, authProvider: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      authProvider.completeNewPasswordChallenge(loginRequest.password, [], {
        onSuccess: (result: any) => {
          resolve(result);
        },
        onFailure: (error: any) => {
          reject(error);
        },
      });
    });
  }, []);

  const login = useCallback(
    async ({ username, password }: LoginRequest) => {
      const response = await Auth.signIn(username, password);
      let responseSession = response.signInUserSession;

      if (response.challengeName === "NEW_PASSWORD_REQUIRED") {
        const newPasswordResponse = await completeUpdatePassword({ username, password }, response);
        responseSession = newPasswordResponse;
      }

      setSession({
        username,
        accessToken: responseSession.idToken.jwtToken,
        clientType: "app",
        issuedAt: responseSession.idToken.payload.iat,
        expiresIn: responseSession.idToken.payload.exp,
        refreshToken: responseSession.refreshToken.token,
      });
    },
    [completeUpdatePassword, setSession]
  );

  const register = useCallback(({ username, password, name }: RegisterRequest) => {
    return Auth.signUp({
      username,
      password,
      attributes: {
        name: name,
        email: username,
      },
    }).then((response) => Promise.resolve());
  }, []);

  const refresh = useCallback((): Promise<AuthSession> => {
    if (!sessionRef.current) {
      return Promise.reject(errorMessages.auth.unauthorized);
    }

    const { username, refreshToken, clientType } = sessionRef.current;

    const userClientType = clientType ? clientType : "app";
    return axios
      .post<AuthSession>(
        `auth/refresh`,
        {
          username,
          refreshToken,
          clientType: userClientType,
        },
        {
          baseURL: environment.newApiUrl,
        }
      )
      .then((response) => {
        setSession({ ...response.data, username, clientType: userClientType });

        return response.data;
      })
      .catch((error) => {
        setSession(null);
        throw error;
      });
  }, [setSession]);

  const resetPassword = useCallback((request: VerificationCodeRequest) => {
    return Auth.forgotPassword(request.username).then((response) => Promise.resolve());
  }, []);

  const verifyResetPassword = useCallback(
    (request: VerifyResetRequest) => {
      return Auth.forgotPasswordSubmit(request.username, request.verificationCode, request.password).then((response) => login(request));
    },
    [login]
  );

  const verifySignUp = useCallback(
    (request: VerifyRegisterRequest) => {
      return Auth.confirmSignUp(request.username, request.verificationCode).then((response) => login(request));
    },
    [login]
  );

  const resendSignUpCode = useCallback((request: VerificationCodeRequest) => {
    return Auth.resendSignUp(request.username).then((response) => Promise.resolve());
  }, []);

  const signOut = useCallback(() => {
    return Auth.signOut({ global: true })
      .catch((err) => /**Just ignore any errors as we are clearing the session regardless */ null)
      .then(() => {
        triggerAppEvent(AppEvent.signOut);
        setSession(null);
        queryClient.clear();
      });
  }, [setSession, queryClient]);

  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    const authUser = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(authUser, request.password, request.newPassword);
  }, []);

  const contextValue: AuthContext = {
    session,
    setSession,
    isAuthReady,
    login,
    register,
    refresh,
    verifySignUp,
    resendSignUpCode,
    resetPassword,
    verifyResetPassword,
    signOut,
    changePassword,
  };

  return <Context.Provider value={{ ...contextValue }}>{props.children}</Context.Provider>;
}

function useAuthContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(errorMessages.context.useHookWithinProvider);
  }
  return context;
}

export { AuthContextProvider, useAuthContext };